import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  CheckSquare,
  Circle,
  Info,
  Milestone,
  Plus,
  Sparkles,
  Trash2,
  UserCheck,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { SidebarTabs } from '@/components/sidebar-tabs';
import { ActivityFeed, toFeedItem, type FeedItem } from '@/components/activity-feed';
import { LogActivityDialog } from '@/components/log-activity-dialog';
import { StatusPill } from '@/components/status-dot';
import { Textarea } from '@/components/textarea';
import { useToast } from '@/components/toast';
import { useConfirm } from '@/components/confirm';
import { ClientForm } from '@/routes/clients/client-form';
import {
  useConvertLead,
  useDeleteLead,
  useLead,
  useUpdateLead,
  type LeadInput,
} from '@/lib/leads';
import { useContactActivity, useDeleteContactActivity } from '@/lib/contact-activity';
import {
  useCreateLeadTask,
  useDeleteLeadTask,
  useUpdateLeadTask,
} from '@/lib/lead-tasks';
import { formatStatus } from '@/lib/format';
import { LeadForm } from './lead-form';
import { SOURCE_LABEL, stageTone } from './index';
import type { ClientData, LeadData, LeadTaskData } from '@/types/api';

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const leadId = id ? Number(id) : undefined;
  const { data, isLoading } = useLead(leadId);
  const update = useUpdateLead();
  const remove = useDeleteLead();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [convertOpen, setConvertOpen] = useState(false);
  const restoreConvert = useConvertLead();

  if (isLoading || !data) {
    return <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>;
  }

  // Narrow the data so closures below get a non-null lead.
  const lead: LeadData = data;
  // A won lead with a client was converted. One that points at a client but
  // is still open came back from that client, which waits archived.
  const converted = lead.stage === 'won' && lead.client_id != null;
  const parked = lead.client_id != null && !converted;

  async function onRestoreClient() {
    const ok = await confirm({
      title: `Win ${lead.name} and restore the client?`,
      body: `${lead.client_name ?? 'The client'} becomes active again, with everything it had. No new client is created.`,
      confirmLabel: 'Win and restore',
    });
    if (!ok) return;
    const client = await restoreConvert.mutateAsync({ leadId: lead.id, payload: {} });
    toast.success(`${client.name} is active again`);
    navigate(`/clients/${client.id}`);
  }

  async function onSaveDetails(input: LeadInput) {
    await update.mutateAsync({ id: lead.id, patch: input });
    toast.success('Lead updated');
  }

  async function onDelete() {
    const ok = await confirm({
      title: `Delete ${lead.name}?`,
      body: 'The lead will be soft-deleted. Activity history is preserved.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    await remove.mutateAsync(lead.id);
    toast.success('Lead deleted');
    navigate('/leads');
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Sparkles className="size-5 shrink-0 text-[var(--color-foreground-muted)]" />
            {lead.name}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[var(--color-foreground-muted)]">
            <StatusPill tone={stageTone(lead.stage)}>{formatStatus(lead.stage)}</StatusPill>
            <Badge tone="neutral">{SOURCE_LABEL[lead.source] ?? lead.source}</Badge>
            {converted ? <Badge tone="success">Converted</Badge> : null}
            {parked ? <Badge tone="neutral">Client archived</Badge> : null}
            {lead.tags.map((t) => (
              <Badge key={t} tone="neutral">
                {t}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {converted && lead.client_id ? (
            <Button variant="secondary" onClick={() => navigate(`/clients/${lead.client_id}`)}>
              <UserCheck className="size-3.5" /> View client
            </Button>
          ) : parked && lead.client_id ? (
            <>
              <Button variant="secondary" onClick={() => navigate(`/clients/${lead.client_id}`)}>
                View client record
              </Button>
              <Button onClick={onRestoreClient} disabled={restoreConvert.isPending}>
                <UserCheck className="size-3.5" /> Win and restore client
              </Button>
            </>
          ) : (
            <Button onClick={() => setConvertOpen(true)}>
              <UserCheck className="size-3.5" /> Convert to client
            </Button>
          )}
          <Button variant="ghost" onClick={onDelete}>
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      <SidebarTabs
        paramName="tab"
        tabs={[
          {
            key: 'activity',
            label: 'Activity',
            icon: Activity,
            content: <ActivityTab lead={lead} />,
          },
          {
            key: 'tasks',
            label: 'Tasks',
            icon: CheckSquare,
            count: lead.open_tasks?.length ?? 0,
            content: <TasksTab lead={lead} />,
          },
          {
            key: 'details',
            label: 'Details',
            icon: Info,
            content: (
              <Card>
                <CardBody>
                  <LeadForm initial={lead} onSubmit={onSaveDetails} submitLabel="Save details" />
                </CardBody>
              </Card>
            ),
          },
        ]}
      />

      {convertOpen ? (
        <ConvertDialog
          lead={lead}
          onClose={() => setConvertOpen(false)}
          onConverted={(client) => {
            setConvertOpen(false);
            toast.success(`${client.name} added to clients`);
            navigate(`/clients/${client.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The lead's own milestones. Everything else in the tab (calls, texts, notes,
 * web inquiries) comes from the contact's feed, which is why converting a lead
 * or opening a second one no longer hides its history.
 */
function stageItems(lead: LeadData): FeedItem[] {
  const stamps: Array<[string | null, string, string, string | null]> = [
    [lead.created_at, 'created', 'Lead created', null],
    [lead.contacted_at, 'contacted', 'Moved to Contacted', null],
    [lead.qualified_at, 'qualified', 'Moved to Qualified', null],
    [lead.won_at, 'won', 'Won', null],
    [lead.lost_at, 'lost', 'Lost', lead.lost_reason],
  ];

  const tones: Record<string, string> = {
    created: 'text-muted-foreground',
    contacted: 'text-blue-600 dark:text-blue-400',
    qualified: 'text-violet-600 dark:text-violet-400',
    won: 'text-emerald-600 dark:text-emerald-400',
    lost: 'text-rose-600 dark:text-rose-400',
  };

  return stamps
    .filter(([at]) => at != null)
    .map(([at, key, title, body]) => ({
      id: `stage-${key}-${lead.id}`,
      at: at as string,
      icon: Milestone,
      iconTone: tones[key],
      title,
      body,
      badge: null,
    }));
}

function ActivityTab({ lead }: { lead: LeadData }) {
  const [addOpen, setAddOpen] = useState(false);
  const { data: feed, isLoading } = useContactActivity(lead.contact_id);
  const del = useDeleteContactActivity(lead.contact_id);
  const confirm = useConfirm();
  const toast = useToast();

  // Stage changes and telephony entries are records of what happened; only the
  // notes logged on this feed are ours to remove.
  const items = useMemo<FeedItem[]>(() => {
    const onDelete = async (activityId: number) => {
      const ok = await confirm({
        title: 'Delete this note?',
        body: 'It is removed from this feed for good.',
        confirmLabel: 'Delete note',
        tone: 'danger',
      });
      if (ok) {
        await del.mutateAsync(activityId);
        toast.success('Note deleted');
      }
    };

    const out = [
      ...stageItems(lead),
      ...(feed ?? []).map((item) => toFeedItem(item, { onDelete })),
    ];
    return out.sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [lead, feed, confirm, del, toast]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Everything that's happened with {lead.name}, newest first.
        </p>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="size-4" /> Log activity
        </Button>
      </div>

      <Card>
        <CardBody>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <EmptyState
              icon={Activity}
              title="Quiet so far"
              description="Log a call, meeting, or note to start the trail."
            />
          ) : (
            <ActivityFeed items={items} />
          )}
        </CardBody>
      </Card>

      {addOpen ? (
        <LogActivityDialog
          contactId={lead.contact_id}
          leadId={lead.id}
          onClose={() => setAddOpen(false)}
        />
      ) : null}
    </div>
  );
}

function TasksTab({ lead }: { lead: LeadData }) {
  const [addOpen, setAddOpen] = useState(false);
  const update = useUpdateLeadTask(lead.id);
  const remove = useDeleteLeadTask(lead.id);
  const toast = useToast();

  const openTasks = (lead.open_tasks ?? []) as LeadTaskData[];

  async function toggle(task: LeadTaskData, completed: boolean) {
    try {
      await update.mutateAsync({ taskId: task.id, patch: { completed } });
      toast.success(completed ? 'Task completed' : 'Marked open');
    } catch {
      toast.error("Couldn't update task");
    }
  }

  async function onDelete(task: LeadTaskData) {
    await remove.mutateAsync(task.id);
    toast.success('Task removed');
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Open follow-ups for this lead. Ticked tasks fall off the list.
        </p>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="size-4" /> New task
        </Button>
      </div>

      {openTasks.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={CheckSquare}
              title="No open tasks"
              description="Add a follow-up so it shows up in the sidebar badge when it's due."
            />
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <ul className="space-y-2">
              {openTasks.map((t) => (
                <li
                  key={t.id}
                  className="group/t flex items-start gap-3 rounded-md border border-border/70 px-3 py-2.5"
                >
                  <button
                    type="button"
                    onClick={() => toggle(t, true)}
                    aria-label="Mark complete"
                    className="mt-0.5 text-muted-foreground transition-colors hover:text-emerald-600"
                  >
                    <Circle className="size-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2 text-sm">
                      <span className="font-medium">{t.title}</span>
                      {t.is_overdue ? (
                        <Badge tone="danger">Overdue</Badge>
                      ) : (
                        <Badge tone="neutral">
                          Due {new Date(t.due_at).toLocaleString()}
                        </Badge>
                      )}
                    </div>
                    {t.notes ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">{t.notes}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(t)}
                    aria-label="Delete task"
                    className="text-muted-foreground opacity-0 transition-opacity group-hover/t:opacity-100 hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {addOpen ? <AddTaskDialog leadId={lead.id} onClose={() => setAddOpen(false)} /> : null}
    </div>
  );
}

function AddTaskDialog({ leadId, onClose }: { leadId: number; onClose: () => void }) {
  const create = useCreateLeadTask(leadId);
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [dueAt, setDueAt] = useState(defaultDueAt());

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!title.trim() || !dueAt) return;
    try {
      await create.mutateAsync({
        title: title.trim(),
        notes: notes.trim() || null,
        due_at: new Date(dueAt).toISOString(),
      });
      toast.success('Task added');
      onClose();
    } catch {
      toast.error("Couldn't save the task");
    }
  }

  return (
    <Dialog open={true} onClose={onClose} title="New task" size="md">
      <form className="space-y-4" onSubmit={onSubmit}>
        <FieldRow label="What needs doing?">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Follow up on proposal"
            required
          />
        </FieldRow>
        <FieldRow label="Due">
          <Input
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            required
          />
        </FieldRow>
        <FieldRow label="Notes (optional)">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </FieldRow>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Add task'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function defaultDueAt(): string {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  d.setMinutes(0, 0, 0);
  return toLocalInput(d);
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}


function ConvertDialog({
  lead,
  onClose,
  onConverted,
}: {
  lead: LeadData;
  onClose: () => void;
  onConverted: (client: ClientData) => void;
}) {
  const convert = useConvertLead();
  const toast = useToast();

  return (
    <Dialog open={true} onClose={onClose} title={`Convert ${lead.name} to a client`} size="lg">
      <p className="mb-4 text-sm text-muted-foreground">
        Pre-filled from the lead. Pick Company or Individual and adjust anything before saving.
      </p>
      <ClientForm
        initial={{
          name: lead.name,
          primary_email: lead.email,
          primary_phone: lead.phone,
        }}
        submitLabel="Create client"
        onSubmit={async (input) => {
          try {
            const client = await convert.mutateAsync({ leadId: lead.id, payload: input });
            onConverted(client);
          } catch {
            toast.error("Couldn't convert lead. Check the fields and try again.");
          }
        }}
      />
    </Dialog>
  );
}
